import Select from '@bugbug/core/components/Select';
import { Trans, useTranslation } from 'react-i18next';

import type { SubscriptionPlan as Plan } from '@bugbug/core/types/subscriptions';

import { getParallelsLabel } from '../Subscription/Subscription.helpers';

import * as S from './ParallelSelect.styled';

interface ParallelSelectProps {
  plan: Plan;
  initialParallelRuns: string;
  selectedParallelRuns: string;
  setSelectedParallelRuns: (value: string) => void;
  variant?: 'regular' | 'large';
  disableCurrentOption?: boolean;
}

export const ParallelSelect = ({
  plan,
  variant = 'regular',
  initialParallelRuns,
  selectedParallelRuns,
  setSelectedParallelRuns,
  disableCurrentOption = false,
}: ParallelSelectProps) => {
  const { t } = useTranslation();

  const renderSelectedOption = (option: { value: string }) => {
    if (plan.type === 'free') {
      return t('subscription.parallelRuns.options.unavailable', 'Not available');
    }

    return getParallelsLabel(parseInt(option.value, 10));
  };

  const Root = variant === 'large' ? S.LargeContainer : Select;

  return (
    <Root
      name="parallelRuns"
      value={selectedParallelRuns}
      onChange={(event) => setSelectedParallelRuns(event.target.value)}
      showSearch
      optionsWidth={316}
      maxOptionsHeight={220}
      anchor="bottom-start"
      disabled={plan.type === 'free'}
      renderSelectedOption={renderSelectedOption}
      data-testid={variant === 'large' ? 'ParallelSelectLarge' : 'ParallelSelect'}
      disableSearchByValue
    >
      <S.Option
        value="0"
        disabled={disableCurrentOption && initialParallelRuns === '0'}
        label={getParallelsLabel(0)}
      >
        <Trans key="subscription.parallelRuns.options.noParallelWithPrice">
          1 test run at a time{' '}
          {initialParallelRuns === '0'
            ? t('subscription.parallelRuns.options.current', '(current)')
            : ''}
          <S.OptionPrice>$0 / mo</S.OptionPrice>
        </Trans>
      </S.Option>
      {plan.possibleAddons
        .filter((a) => a.type === 'extra_parallel_runs_limit')
        .map((a) => {
          const isCurrent = a.value.toString() === initialParallelRuns;

          return (
            <S.Option
              key={a.id}
              value={a.value.toString()}
              disabled={disableCurrentOption && isCurrent}
              label={getParallelsLabel(a.value)}
            >
              <Trans key="subscription.parallelRuns.options.parallelRunsWithPrice">
                {(a.value + 1).toString()} parallel runs{' '}
                {isCurrent ? t('subscription.parallelRuns.options.current', '(current)') : ''}
                <S.OptionPrice>${a.pricePerMonth.toString()} / mo</S.OptionPrice>
              </Trans>
            </S.Option>
          );
        })}
    </Root>
  );
};
